import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { AuthProvider } from "./lib/auth";
import { ContentProvider } from "./lib/content";
import { useSeo } from "./lib/useSeo";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import MobileCheckBar from "./components/MobileCheckBar";

import Home from "./pages/Home";
import CheckResults from "./pages/CheckResults";
import Product from "./pages/Product";
import HowItWorks from "./pages/HowItWorks";
import About from "./pages/About";
import FAQs from "./pages/FAQs";
import InsightsList from "./pages/InsightsList";
import InsightDetail from "./pages/InsightDetail";
import Partnerships from "./pages/Partnerships";
import Pricing from "./pages/Pricing";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import CheckoutCancel from "./pages/CheckoutCancel";
import Account from "./pages/Account";
import CheckoutDetails from "./pages/CheckoutDetails";

import AdminLogin from "./pages/admin/Login";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminOverview from "./pages/admin/Overview";
import AdminInsightsList from "./pages/admin/InsightsList";
import AdminInsightEditor from "./pages/admin/InsightEditor";
import AdminLeads from "./pages/admin/Leads";
import AdminTransactions from "./pages/admin/Transactions";
import AdminContent from "./pages/admin/Content";
import AdminTeam from "./pages/admin/Team";
import AdminReviews from "./pages/admin/Reviews";
import AdminCustomers from "./pages/admin/Customers";
import AdminNbrNames from "./pages/admin/NbrNames";
import AdminEmailDrips from "./pages/admin/EmailDrips";
import AdminEmailTemplates from "./pages/admin/EmailTemplates";
import AdminSeo from "./pages/admin/Seo";
import AdminSecurity from "./pages/admin/Security";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

// ─────────────────────────────────────────────────────────────
// Google Analytics 4 - SPA route tracker
// ─────────────────────────────────────────────────────────────
// GA4's gtag.js snippet in public/index.html sets `send_page_view: false`,
// which disables the automatic initial pageview. This component fires a
// `page_view` event on every route change (INCLUDING the first mount), so
// GA sees a clean, single pageview per URL. It also updates `page_title`
// after React has mounted the new route (document.title is set by useSeo).
// If gtag isn't on window (e.g. an ad-blocker stripped it), this no-ops.
function AnalyticsRouteTracker() {
  const location = useLocation();
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    // Defer one microtask so useSeo's document.title update lands first.
    const t = setTimeout(() => {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }, 0);
    return () => clearTimeout(t);
  }, [location.pathname, location.search]);
  return null;
}

function PublicShell({ children }) {
  return (
    <>
      <Navbar />
      {children}
      <Footer />
      <MobileCheckBar />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ContentProvider>
            <ScrollToTop />
            <AnalyticsRouteTracker />
          <Routes>
            {/* Public site */}
            <Route path="/" element={<PublicShell><Home /></PublicShell>} />
            <Route path="/check" element={<PublicShell><CheckResults /></PublicShell>} />
            <Route path="/business-name-protection" element={<PublicShell><Product /></PublicShell>} />
            <Route path="/how-it-works" element={<PublicShell><HowItWorks /></PublicShell>} />
            <Route path="/pricing" element={<PublicShell><Pricing /></PublicShell>} />
            <Route path="/about" element={<PublicShell><About /></PublicShell>} />
            <Route path="/faqs" element={<PublicShell><FAQs /></PublicShell>} />
            <Route path="/insights" element={<PublicShell><InsightsList /></PublicShell>} />
            <Route path="/insights/:slug" element={<PublicShell><InsightDetail /></PublicShell>} />
            <Route path="/partnerships" element={<PublicShell><Partnerships /></PublicShell>} />
            <Route path="/checkout/details" element={<PublicShell><CheckoutDetails /></PublicShell>} />
            <Route path="/checkout/success" element={<PublicShell><CheckoutSuccess /></PublicShell>} />
            <Route path="/checkout/cancel" element={<PublicShell><CheckoutCancel /></PublicShell>} />
            <Route path="/account" element={<PublicShell><Account /></PublicShell>} />

            {/* Admin */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminOverview />} />
              <Route path="insights" element={<AdminInsightsList />} />
              <Route path="insights/new" element={<AdminInsightEditor />} />
              <Route path="insights/:id/edit" element={<AdminInsightEditor />} />
              <Route path="leads" element={<AdminLeads />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="email-drips" element={<AdminEmailDrips />} />
              <Route path="email-templates" element={<AdminEmailTemplates />} />
              <Route path="seo" element={<AdminSeo />} />
              <Route path="transactions" element={<AdminTransactions />} />
              <Route path="content" element={<AdminContent />} />
              <Route path="team" element={<AdminTeam />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="nbr-names" element={<AdminNbrNames />} />
              <Route path="security" element={<AdminSecurity />} />
            </Route>

            <Route path="*" element={<PublicShell><NotFound /></PublicShell>} />
          </Routes>
          <Toaster position="top-right" richColors closeButton />
          </ContentProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

function NotFound() {
  useSeo({ title: "Page not found", noindex: true });
  return (
    <main className="py-24 text-center" data-testid="not-found-page">
      <p className="font-mono-brand text-sm tracking-widest uppercase text-[#447694]">404</p>
      <h1 className="font-display text-[clamp(2.25rem,4.5vw,4rem)] text-[#04273d] mt-3 font-bold tracking-tight">Page not found.</h1>
      <p className="text-[#447694] mt-3">The page you&rsquo;re looking for may have been moved or removed.</p>
      <a href="/" className="btn-primary inline-flex mt-6">Back to home</a>
    </main>
  );
}

export default App;
